import React from 'react';
import PropTypes from 'prop-types';

import Complete from './complete';
import Progress from './progress';
import Password from './password';
import Expired from './expired';
import Invalid from './invalid';
import Spam from './spam';
import Files from './files';
import Feedback from './feedback';
import Blocked from './blocked';

import AppBar from '@component/info/appbar';
import { shouldTriggerAppNotification } from '@component/info/appbar-hoc';
import Store from '@app/store';
import Actions from '@app/action';

// If the transfer is processing, we'll poll for updates.
const POLL_INTERVAL = 2500;
export default class Downloader extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    secret: PropTypes.string.isRequired,
    recipient_id: PropTypes.string,
    file_id: PropTypes.string,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      showAppBar: false,
    };

    this.pollTransferDetails = this.pollTransferDetails.bind(this);
  }

  // When this component gets mounted, we'll see if there is already sideloaded information
  // available to speed up the process.
  componentDidMount() {
    // By default we'll have an empty transfer that is processing.
    let transferDetails = {
      state: 'processing',
    };

    // Get the sideloaded Transfer information and remove the global object.
    if (
      window['_preloaded_transfer_'] &&
      typeof window['_preloaded_transfer_'] === 'object'
    ) {
      transferDetails = window['_preloaded_transfer_'];
      window['_preloaded_transfer_'] = null;
    }

    // Create a new Transfer object with the sideloaded data.
    Store.dispatch(Actions.Transfer.buildTransfer(transferDetails));

    // Get the transfer from our store
    const { transfer } = Store.getState();

    this.triggerAppNotification(transfer);

    // No longer processing, done or error
    if (
      transfer.state !== 'processing' &&
      transfer.state !== 'password_protected'
    ) {
      return;
    }

    // Load Transfer Details from the API if it's still processing
    this.pollTransferDetails();
  }

  // When this component gets unmounted, we can stop the polling timer if it's active
  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  componentDidUpdate() {
    // Stop timer if the state is already downloadable
    const { transfer } = Store.getState();
    if (transfer.state === 'downloadable') {
      clearTimeout(this.timer);
    }
  }

  // Load Transfer details for our current transfer
  pollTransferDetails() {
    const { transfer, download } = Store.getState();

    if (transfer.state === 'password_protected' && !transfer.password) {
      // Try again after the INTERVAL
      clearTimeout(this.timer);
      this.timer = setTimeout(this.pollTransferDetails, POLL_INTERVAL);
      return;
    }

    // If the current transfer state is not processing, we don't have to poll
    if (transfer.state !== 'processing') {
      clearTimeout(this.timer);
      return;
    }

    // Only send a new request if we're not still waiting for one.
    if (!download.isPending) {
      // Load the Transfer data from our API
      const password = transfer.password || false;
      const secret =
        (transfer.versioning && transfer.versioning.latest) ||
        transfer.version ||
        this.props.secret;
      Store.dispatch(
        Actions.Transfer.loadTransfer(
          this.props.id,
          secret,
          this.props.recipient_id,
          password
        )
      );
    }

    // Try again after the INTERVAL
    clearTimeout(this.timer);
    this.timer = setTimeout(this.pollTransferDetails, POLL_INTERVAL);
  }

  triggerAppNotification(transfer = {}) {
    if (!shouldTriggerAppNotification(transfer)) {
      return;
    }

    const panel = document.querySelector('.panel__content');
    if (panel) {
      panel.classList.add('panel__content--mobile-popup');
    }
    this.setState({ showAppBar: true });
  }

  render() {
    const { transfer } = Store.getState();
    let component = null;
    let appBar = null;
    if (!transfer.state) {
      return false;
    }

    if (this.state.showAppBar && transfer.state === 'downloadable') {
      appBar = <AppBar />;
    }

    switch (transfer.state) {
      // Transfer is still in processing.
      case 'processing':
      case 'init':
        component = (
          <Files
            isReady={false}
            transfer={transfer}
            id={this.props.id}
            secret={transfer.version || this.props.secret}
            recipient={this.props.recipient_id}
          />
        );
        break;

      // The transfer is no longer available for download
      case 'expired':
        component = <Expired transfer={transfer} />;
        break;

      // This is not a valid transfer (404 I guess)
      case 'invalid':
        component = <Invalid transfer={transfer} />;
        break;

      // This transfer was reported as spam
      case 'spam':
        component = <Spam transfer={transfer} />;
        break;

      case 'password_protected':
        component = (
          <Password
            transfer={transfer}
            id={this.props.id}
            secret={transfer.version || this.props.secret}
            recipient={this.props.recipient_id}
          />
        );
        break;

      // The transfer is downloadable, show a summary of files
      case 'downloadable':
        component = (
          <Files
            isReady={true}
            transfer={transfer}
            id={this.props.id}
            secret={transfer.version || this.props.secret}
            recipient={this.props.recipient_id}
            file_id={this.props.file_id}
          />
        );
        break;

      // Download has started.
      case 'downloading':
        // You started the download
        component = <Progress transfer={transfer} />;
        break;

      // Download finished
      case 'finished':
      case 'feedbackComplete':
        // The download has started in the browsers but is finished from our point of view
        component = <Complete />;
        break;

      case 'feedback':
        component = <Feedback />;
        break;

      case 'blocked':
        component = <Blocked />;
        break;
    }

    return (
      <div>
        {component}
        {appBar}
      </div>
    );
  }
}
